import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    CircularProgress,
    Typography,
    Snackbar,
    Alert,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import axiosInstance from '../utils/axios';
import ContainerComponent from './LoginContainer';

const EmailResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [helperText, setHelperText] = useState('');

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Extract email and code from URL parameters
    useEffect(() => {
        const emailParam = searchParams.get('email');
        const tokenParam = searchParams.get('token');

        if (!emailParam || !tokenParam) {
            // If email or code is missing, redirect to forgot password page
            setSnackbar({
                open: true,
                message: "Invalid reset link. Please try again.",
                severity: "error",
            });
            setTimeout(() => {
                navigate("/forgot-password");
            }, 2000);
            return;
        }

        setEmail(emailParam);
        setToken(tokenParam);
    }, [searchParams, navigate]);

    const validatePasswords = (newPass, confirmPass) => {
        if (confirmPass && newPass !== confirmPass) {
            setPasswordError(true);
            setHelperText("Passwords do not match");
            return false;
        } else {
            setPasswordError(false);
            setHelperText('');
            return true;
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        try {
            setBtnLoading(true);

            if (newPassword !== confirmPassword) {
                setSnackbar({
                    open: true,
                    message: "Password mismatch",
                    severity: "error",
                });
                return;
            }

            if (!newPassword || !confirmPassword) {
                setSnackbar({
                    open: true,
                    message: "Please fill in all fields",
                    severity: "error",
                });
                return;
            }

            const res = await axiosInstance.get(`/auth/forget-password?token=${token}&email=${email}&password=${newPassword}`);

            if (res.status === 200) {
                setSnackbar({
                    open: true,
                    message: "Password reset successfully! Redirecting to login...",
                    severity: "success",
                });

                setTimeout(() => {
                    navigate("/login");
                }, 2000);
            } else {
                setSnackbar({
                    open: true,
                    message: "Failed to reset password. Please try again.",
                    severity: "error",
                });
            }
        } catch (err) {
            console.error('Reset password error:', err);

            let errorMessage = "Something went wrong. Please try again.";
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.status === 400) {
                errorMessage = "Invalid or expired reset code. Please request a new reset link.";
            } else if (err.response?.status === 404) {
                errorMessage = "User not found. Please check your email and try again.";
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: "error",
            });
        } finally {
            setBtnLoading(false);
        }
    };

    // Check if form is valid
    const isFormValid = newPassword && confirmPassword && !passwordError && email && token
    return (
        <ContainerComponent>
            <Box
                sx={{
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: '#f5f5f5',
                }}
            >
                <Box
                    component="form"
                    onSubmit={onSubmit}
                    sx={{
                        width: 432,
                        p: 4,
                        backgroundColor: '#fff',
                        borderRadius: 2,
                        boxShadow: 3,
                    }}
                >
                    <Box sx={{ mb: 5, textAlign: 'center' }}>
                        <img src="/suryalogo.svg" alt="Surya Logo" width={230} height={50} />
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 'bold',
                            mb: 1,
                            textAlign: 'center',
                            fontSize: '24px'
                        }}
                    >
                        Reset Password
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            color: 'text.secondary',
                            mb: 3
                        }}
                    >
                        Enter your new password below
                    </Typography>

                    <TextField
                        fullWidth
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Enter New Password"
                        variant="outlined"
                        required
                        value={newPassword}
                        onChange={(e) => {
                            const value = e.target.value;
                            setNewPassword(value);
                            validatePasswords(value, confirmPassword);
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                fontSize: '16px',
                                '& input': {
                                    padding: '10px 14px',
                                },
                            },
                        }}
                        InputLabelProps={{
                            sx: { fontSize: '16px' },
                        }}
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Re-Enter New Password"
                        variant="outlined"
                        required
                        value={confirmPassword}
                        error={passwordError}
                        helperText={passwordError ? helperText : ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            setConfirmPassword(value);
                            validatePasswords(newPassword, value);
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: {
                                fontSize: '16px',
                                '& input': {
                                    padding: '10px 14px',
                                },
                            },
                        }}
                        InputLabelProps={{
                            sx: { fontSize: '16px' },
                        }}
                        FormHelperTextProps={{
                            sx: { fontSize: '14px' },
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={!isFormValid || btnLoading}
                        sx={{
                            backgroundColor: isFormValid ? '#f16253' : '#ccc',
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: 500,
                            py: 1.5,
                            textTransform: 'none',
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: isFormValid ? '#d1534b' : '#ccc',
                            },
                            '&:disabled': {
                                backgroundColor: '#ccc',
                                cursor: 'not-allowed',
                            },
                        }}
                    >
                        {btnLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} color="inherit" />
                                Resetting...
                            </Box>
                        ) : (
                            'Reset Password'
                        )}
                    </Button>

                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Button
                            variant="text"
                            onClick={() => navigate('/login')}
                            disabled={btnLoading}
                            sx={{
                                color: '#f16253',
                                fontSize: '14px',
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#fff2f1',
                                },
                            }}
                        >
                            Back to Login
                        </Button>
                    </Box>
                </Box>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ContainerComponent>
     
    );
}

export default EmailResetPassword;